import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { services } from "../data/services";
import { locations } from "../data/locations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ compact = false, defaultService = "", sourcePage = "/" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", service: defaultService, location: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, source_page: sourcePage });
      setSent(true);
      toast.success("Quote sent — we'll be in touch within 24 hours");
      setForm({ name: "", email: "", phone: "", postcode: "", service: defaultService, location: "", message: "" });
    } catch (err) {
      toast.error("Could not submit — please call us directly");
    } finally {
      setLoading(false);
    }
  };

  if (sent && compact) {
    return <div className="lc-card p-6 bg-dark text-white text-center" data-testid="quote-success-compact"><p className="font-display text-2xl">Thanks — we've got it.</p><p className="text-white/70 text-sm mt-2">We'll be in touch within 24 hours.</p></div>;
  }

  return (
    <form onSubmit={submit} className={`lc-card p-6 lg:p-8 ${compact ? "" : ""}`} data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Full name *" value={form.name} onChange={set("name")} testid="quote-name" />
        <Input placeholder="Phone *" type="tel" value={form.phone} onChange={set("phone")} testid="quote-phone" />
        <Input placeholder="Email *" type="email" value={form.email} onChange={set("email")} testid="quote-email" />
        <Input placeholder="Postcode" value={form.postcode} onChange={set("postcode")} testid="quote-postcode" />
        <Select value={form.service} onChange={set("service")} testid="quote-service">
          <option value="">Service interested in…</option>
          {services.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
        </Select>
        <Select value={form.location} onChange={set("location")} testid="quote-location">
          <option value="">Your area</option>
          {locations.map((l) => <option key={l.slug} value={l.name}>{l.name}</option>)}
        </Select>
      </div>
      <textarea
        placeholder="Tell us about your project (optional)"
        value={form.message}
        onChange={set("message")}
        rows={3}
        data-testid="quote-message"
        className="mt-3 w-full px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30"
      />
      <button type="submit" disabled={loading} className="btn-primary mt-4 w-full sm:w-auto" data-testid="quote-submit">
        {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />} {loading ? "Sending…" : "Get my free quote"}
      </button>
      <p className="text-xs text-dark/55 mt-3">No-obligation. We'll respond within 24 hours. Your data stays private.</p>
    </form>
  );
}

const Input = ({ testid, ...p }) => <input {...p} data-testid={testid} className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />;
const Select = ({ children, testid, ...p }) => <select {...p} data-testid={testid} className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30">{children}</select>;
